import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { searchApiDebounced, cancelSearch } from "../../store/thunks/searchApiThunk";
import { setQuery, clearResults } from "../../store/slices/searchApiSlice";

function Search({ className, placeholder = "Search...", onResultClick }) {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const { results, loading, error } = useSelector((state) => state.search);

  useEffect(() => {
    if (searchQuery) {
      setShowResults(true);
    } else {
      setShowResults(false);
    }
  }, [searchQuery, results]);

  // Cleanup function to cancel search when component unmounts
  useEffect(() => {
    return () => {
      cancelSearch();
    };
  }, []);

  const handleSearchChange = (event) => {
    const newQuery = event.target.value;
    setSearchQuery(newQuery);
    dispatch(setQuery(newQuery));
    
    if (newQuery && newQuery.length > 0) {
      // Use debounced search instead of immediate search
      dispatch(searchApiDebounced(newQuery));
    } else {
      dispatch(clearResults());
    }
  };

  const handleResultClick = (result) => {
    console.log("Clicked result:", result);
    setShowResults(false);
    onResultClick(result); // Pass the clicked result back to the parent component
  };

  const handleBubbleClick = (item, event) => {
    event.stopPropagation();
    setShowResults(false);
    setSearchQuery("");
    dispatch(setQuery(""));
    
    // Use the existing onResultClick callback which should call handleSearchResultClick in App.js
    if (onResultClick) {
      onResultClick(item);
    } else {
      console.warn("No onResultClick callback provided to Search component");
    }
  };

  useEffect(() => {
    if (results.length > 0) {
      // console.log("Search results:", results);
    }
  }, [results]);

  const getTypeColor = (type) => {
    switch (type) {
      case "Client":
        return "bg-blue-100 text-blue-800";
      case "Appointment":
        return "bg-green-100 text-green-800";
      case "Product":
        return "bg-yellow-100 text-yellow-800";
      case "Service":
        return "bg-yellow-100 text-orange-800";
      case "Invoice":
        return "bg-red-100 text-red-800";
      case "Clinic":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setSearchQuery("");
      setShowResults(false);
      dispatch(setQuery(""));
    }, 200); // Delay to allow click event to fire
  };

  return (
    <div className={className}>
      <input
        type="text"
        name="search"
        id="search"
        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-12 sm:text-sm border-gray-300 rounded-sm"
        placeholder={placeholder}
        value={searchQuery}
        onChange={handleSearchChange}
        onFocus={() => setShowResults(true)}
        onBlur={handleBlur}
        autoComplete="off"
      />

      {showResults && (results.length > 0 || loading) && (
        <ul className="absolute z-50 mt-2 w-full bg-white rounded-sm shadow-lg max-h-60 overflow-y-auto">
          {loading && (
            <li className="px-4 py-2 text-center text-gray-500">
              <div className="flex items-center justify-center">
                <div className="h-4 w-4 animate-spin rounded-sm border-2 border-gray-300 border-t-indigo-600 mr-2" />
                Loading...
              </div>
            </li>
          )}
          {!loading && results.length === 0 && searchQuery && (
            <li className="px-4 py-2 text-center text-gray-500">
              No results found
            </li>
          )}
          {results.slice(0, 15).map((result, index) => (
            <li
              key={index}
              className="px-4 py-2 cursor-pointer hover:bg-gray-100 relative"
              onClick={() => handleResultClick(result)}>
              <span
                className={`absolute top-2 right-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(
                  result.type
                )}`}>
                {result.type}
              </span>

              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTypeColor(
                  result.type
                )} mr-2 cursor-pointer hover:opacity-80`}
                onClick={(e) => handleBubbleClick(result, e)}>
                {result.type}
              </span>
              <span className="ml-4">
                {result.type === "Client" && (
                  <>
                    <span
                      className={`${getTypeColor(
                        "Client"
                      )} rounded-full px-3 py-1 mr-2 cursor-pointer hover:opacity-80`}
                      onClick={(e) => handleBubbleClick(result, e)}>
                      {`${result.firstName || "N/A"} ${
                        result.lastName || "N/A"
                      }`}
                    </span>
                    <span
                      className={`bg-gray-100 text-gray-800 rounded-full px-3 py-1 mr-2 cursor-pointer hover:opacity-80`}
                      onClick={(e) => handleBubbleClick(result, e)}>
                      {result.phoneNumbers?.[0]?.number || "N/A"}
                    </span>
                    <span
                      className={`bg-gray-100 text-gray-800 rounded-full px-3 py-1 cursor-pointer hover:opacity-80`}
                      onClick={(e) => handleBubbleClick(result, e)}>
                      {result.email || "N/A"}
                    </span>
                  </>
                )}
                {result.type === "Appointment" && (
                  <>
                    <span
                      className={`${getTypeColor(
                        "Appointment"
                      )} rounded-full px-3 py-1 mr-2 cursor-pointer hover:opacity-80`}
                      onClick={(e) => handleBubbleClick(result, e)}>
                      {result.title || "N/A"}
                    </span>
                    <span
                      className={`${getTypeColor(
                        "Client"
                      )} rounded-full px-3 py-1 cursor-pointer hover:opacity-80`}
                      onClick={(e) => handleBubbleClick({...result.client, type: "Client"}, e)}>
                      {`${result.client?.firstName || "N/A"} ${
                        result.client?.lastName || "N/A"
                      }`}
                    </span>
                    {" - "}
                    <span
                      className={`${getTypeColor(
                        "User"
                      )} rounded-full px-3 py-1 cursor-pointer hover:opacity-80`}
                      onClick={(e) => handleBubbleClick({...result.clinician, type: "User"}, e)}>
                      {`${result.clinician?.firstName || "N/A"} ${
                        result.clinician?.lastName || "N/A"
                      }`}
                    </span>
                  </>
                )}
                {result.type === "Product" && (
                  <span
                    className="cursor-pointer hover:opacity-80"
                    onClick={(e) => handleBubbleClick(result, e)}>
                    {`${result.catalogCode || "N/A"} - ${result.item || "N/A"}`}
                  </span>
                )}
                {result.type === "Service" && (
                  <span
                    className="cursor-pointer hover:opacity-80"
                    onClick={(e) => handleBubbleClick(result, e)}>
                    {`${result.serviceCode || "N/A"} - ${
                      result.description || "N/A"
                    }`}
                  </span>
                )}
                {result.type === "Invoice" && (
                  <>
                    <span
                      className={`${getTypeColor(
                        "Invoice"
                      )} rounded-full px-3 py-1 mr-2 cursor-pointer hover:opacity-80`}
                      onClick={(e) => handleBubbleClick(result, e)}>
                      {result.invoiceId || "N/A"}
                    </span>
                    <span
                      className={`${getTypeColor(
                        "Client"
                      )} rounded-full px-3 py-1 mr-2 cursor-pointer hover:opacity-80`}
                      onClick={(e) => handleBubbleClick({...result.client, type: "Client"}, e)}>
                      {`${result.client?.firstName || "N/A"} ${
                        result.client?.lastName || "N/A"
                      }`}
                    </span>
                    <span
                      className={`${getTypeColor(
                        "User"
                      )} rounded-full px-3 py-1 cursor-pointer hover:opacity-80`}
                      onClick={(e) => handleBubbleClick({...result.clinician, type: "User"}, e)}>
                      {`${result.clinician?.firstName || "N/A"} ${
                        result.clinician?.lastName || "N/A"
                      }`}
                    </span>
                  </>
                )}
                {result.type === "Clinic" && (
                  <span
                    className="cursor-pointer hover:opacity-80"
                    onClick={(e) => handleBubbleClick(result, e)}>
                    {result.name || "N/A"}
                  </span>
                )}
                {result.type === "User" && (
                  <span
                    className="cursor-pointer hover:opacity-80"
                    onClick={(e) => handleBubbleClick(result, e)}>
                    {`${result.firstName || "N/A"} ${result.lastName || "N/A"}`}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {error && <div className="text-red-500 text-sm mt-1">Error: {error}</div>}
    </div>
  );
}

export default Search;
